import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { dealersApi } from '../../../api/dealers.api';
import { installationsApi } from '../../../api/installations.api';
import type { CreateTicketInput } from '../../../api/serviceTickets.api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateTicketInput) => Promise<void>;
  error?: string | null;
}

const TYPES = ['breakdown', 'preventive_maintenance', 'warranty', 'amc', 'complaint', 'inspection'];
const PRIORITIES = ['critical', 'high', 'medium', 'low'];
const SOURCES = ['call', 'email', 'portal', 'mobile', 'field'];

export function ServiceTicketFormDialog({ open, onClose, onSubmit, error }: Props) {
  const [dealerId, setDealerId] = useState<number | ''>('');
  const [installationId, setInstallationId] = useState<number | ''>('');
  const [ticketType, setTicketType] = useState('complaint');
  const [priority, setPriority] = useState('medium');
  const [source, setSource] = useState('call');
  const [description, setDescription] = useState('');

  const dealers = useQuery({ queryKey: ['dealers', 'svc-picker'], queryFn: () => dealersApi.list({ status: 'approved', limit: 100 }), enabled: open });
  const installs = useQuery({ queryKey: ['installations', 'svc-picker'], queryFn: () => installationsApi.list({ limit: 100 }), enabled: open });

  useEffect(() => {
    if (!open) { setDealerId(''); setInstallationId(''); setTicketType('complaint'); setPriority('medium'); setSource('call'); setDescription(''); }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New Service Ticket</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField select label="Installation (optional — maps dealer/site/product)" value={installationId} onChange={(e) => setInstallationId(Number(e.target.value))} fullWidth>
            <MenuItem value="">—</MenuItem>
            {(installs.data?.data ?? []).map((i) => <MenuItem key={i.id} value={i.id}>{i.requestCode}</MenuItem>)}
          </TextField>
          <TextField select label="Dealer" value={dealerId} onChange={(e) => setDealerId(Number(e.target.value))} fullWidth helperText="Required if no installation selected">
            <MenuItem value="">—</MenuItem>
            {(dealers.data?.data ?? []).map((d) => <MenuItem key={d.id} value={d.id}>{d.dealerName} ({d.dealerCode})</MenuItem>)}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField select label="Type" value={ticketType} onChange={(e) => setTicketType(e.target.value)} fullWidth>
              {TYPES.map((t) => <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>)}
            </TextField>
            <TextField select label="Priority" value={priority} onChange={(e) => setPriority(e.target.value)} fullWidth>
              {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
            <TextField select label="Source" value={source} onChange={(e) => setSource(e.target.value)} fullWidth>
              {SOURCES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField label="Complaint description" value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline minRows={3} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!dealerId && !installationId}
          onClick={() => onSubmit({ dealerId: dealerId ? Number(dealerId) : undefined, installationId: installationId ? Number(installationId) : undefined, ticketType, priority, source, complaintDescription: description || undefined })}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
